/**
 * SENDA de aprendizaje de un curso (local-first): la espina F2-A se recorre
 * unit→course_module en orden — los módulos `lesson` son nodos del camino
 * serpenteante (estilo Duolingo) y los `resource` son paradas de material.
 * XP/racha del alumno y reanudar por `last_block_id`. Al abrir una lección
 * lanza el `LessonPlayer`. Todo desde PowerSync (offline).
 */
import { type Component, createMemo, createSignal, For, type JSX, Show } from "solid-js";
import { msg } from "@lingui/core/macro";
import {
  Award,
  BookOpen,
  Check,
  ClipboardCheck,
  Film,
  Flame,
  MessageSquare,
  Play,
  Radio,
  Star,
  ToggleLeft,
} from "lucide-solid";
import type { CourseModule, Lesson, VideoWatchProgress } from "@space/powersync";
import { formatTime, isCourseCompleted, validateVideoModuleConfig } from "@space/shared";
import { Link } from "@/lib/u-router";
import { Skeleton } from "@/components/ui/skeleton";
import { Mascot } from "@/components/ui/mascot";
import { useI18n } from "@/providers/i18n-provider";
import {
  useCourseCertificate,
  useCourseProgress,
  useCourseTree,
  useLearnerStats,
  useLessonBlocks,
} from "../../hooks/use-course-data";
import { useCourseModules } from "@/features/academy/hooks/use-course-modules";
// FLAGS: gating por feature de los kinds de módulo (registry-driven).
import { useModuleKindFeatures } from "@/features/flags/use-module-kind-features";
import { ResourceModuleViewer } from "@/features/academy/modules/resource-module";
// F3-C: player del módulo `forum`
import { ForumModuleViewer } from "@/features/academy/modules/forum-module";
// F3-B: player del módulo `assignment` (entrega + nota). Sin esta parada el
// kind caía al genérico "Material" y la tarea era in-entregable desde la senda.
import { AssignmentModulePlayer } from "@/features/academy/modules/assignment-module";
// MEDIA-F3F: módulo `video` en la senda + reanudación
import { VideoModuleViewer } from "@/features/media/components/video-module";
// F3-G: parada `live_session` en la senda (info + Entrar en vivo + grabación).
// Sin esta parada el kind caía al genérico "Material" (LF-B).
import { LiveSessionModuleViewer } from "@/features/academy/modules/live-session-module";
import { useAllWatchProgress } from "@/features/media/hooks/use-media";
import { LessonPlayer } from "./lesson-player";

/** Entrada de la senda: módulo de la espina (o lección legada sin módulo). */
interface LearnEntry {
  key: string;
  kind: string;
  position: number;
  module: CourseModule | null;
  lesson: Lesson | null;
}

export const CourseLearnView: Component<{ courseId: string }> = (props) => {
  const { t } = useI18n();
  const courseId = () => props.courseId;
  const { course, units, lessons } = useCourseTree(courseId);
  const modules = useCourseModules(courseId);
  // FLAGS: estado de feature por kind (org activa + overrides del curso).
  const kindFeature = useModuleKindFeatures(courseId);
  const progress = useCourseProgress(courseId);
  const stats = useLearnerStats();
  const [activeLessonId, setActiveLessonId] = createSignal<string | null>(null);
  const [openResourceId, setOpenResourceId] = createSignal<string | null>(null);

  // MEDIA-F3F: progreso de visionado por vídeo (badge "continuar en…").
  const watchAll = useAllWatchProgress();
  const watchByVideo = createMemo(() => {
    const map = new Map<string, VideoWatchProgress>();
    for (const w of watchAll.data ?? []) {
      if (w.video_id) {
        map.set(w.video_id, w);
      }
    }
    return map;
  });
  const videoAssetIdOf = (module: CourseModule): string => {
    if (!module.config) {
      return "";
    }
    try {
      const parsed: unknown = JSON.parse(module.config);
      const res = validateVideoModuleConfig(parsed);
      return res.ok ? (res.value.videoAssetId ?? "") : "";
    } catch {
      return "";
    }
  };

  const progressByLesson = createMemo(() => {
    const map = new Map<
      string,
      { status: string; lastBlockId: string | null; id: string; score: number }
    >();
    for (const p of progress.data ?? []) {
      map.set(p.lesson_id ?? "", {
        status: p.status ?? "in_progress",
        lastBlockId: p.last_block_id ?? null,
        id: p.id,
        score: p.score ?? 0,
      });
    }
    return map;
  });

  const lessonById = createMemo(() => {
    const map = new Map<string, Lesson>();
    for (const l of lessons.data ?? []) {
      map.set(l.id, l);
    }
    return map;
  });

  /**
   * Senda por unidad (espina F2-A): módulos VISIBLES en orden de posición. Los
   * módulos `lesson` resuelven su detalle por ref_id; las lecciones sin módulo
   * (datos legados aún sin backfill local) se anexan para no ocultar contenido.
   */
  const spineByUnit = createMemo(() => {
    const map = new Map<string, LearnEntry[]>();
    const referenced = new Set<string>();
    for (const m of modules.data ?? []) {
      if (m.visibility === "hidden") {
        if (m.kind === "lesson" && m.ref_id) {
          referenced.add(m.ref_id);
        }
        continue;
      }
      const unitId = m.unit_id ?? "";
      const arr = map.get(unitId) ?? [];
      const lesson = m.kind === "lesson" && m.ref_id ? (lessonById().get(m.ref_id) ?? null) : null;
      if (m.kind === "lesson" && m.ref_id) {
        referenced.add(m.ref_id);
      }
      if (m.kind === "lesson" && !lesson) {
        continue;
      }
      arr.push({
        key: m.id,
        kind: m.kind ?? "lesson",
        position: m.position ?? 0,
        module: m,
        lesson,
      });
      map.set(unitId, arr);
    }
    for (const l of lessons.data ?? []) {
      if (referenced.has(l.id)) {
        continue;
      }
      const unitId = l.unit_id ?? "";
      const arr = map.get(unitId) ?? [];
      arr.push({
        key: `legacy-${l.id}`,
        kind: "lesson",
        position: l.position ?? 0,
        module: null,
        lesson: l,
      });
      map.set(unitId, arr);
    }
    for (const arr of map.values()) {
      arr.sort((a, b) => a.position - b.position);
    }
    return map;
  });

  /** Lecciones que la senda realmente muestra (progreso/completitud). */
  const spineLessons = createMemo(() => {
    const out: Lesson[] = [];
    for (const u of units.data ?? []) {
      for (const e of spineByUnit().get(u.id) ?? []) {
        if (e.kind === "lesson" && e.lesson) {
          out.push(e.lesson);
        }
      }
    }
    return out;
  });

  const totalLessons = () => spineLessons().length;
  const completedCount = () => (progress.data ?? []).filter((p) => p.status === "completed").length;

  // Curso COMPLETO (espejo puro del criterio del servidor: todas las lecciones
  // aprobadas) → aflora el certificado emitido server-side (llega por sync).
  const courseDone = createMemo(() =>
    isCourseCompleted(
      (lessons.data ?? []).map((l) => ({ id: l.id, deleted_at: l.deleted_at })),
      (progress.data ?? []).map((p) => ({
        lesson_id: p.lesson_id,
        status: p.status,
        score: p.score,
      })),
    ),
  );
  const certificate = useCourseCertificate(courseId);

  // Índice de la primera lección no superada → nodo "actual" (destacado).
  const currentLessonId = createMemo(() => {
    for (const l of spineLessons()) {
      if (progressByLesson().get(l.id)?.status !== "completed") {
        return l.id;
      }
    }
    return null;
  });

  const activeLesson = createMemo(
    () => (lessons.data ?? []).find((l) => l.id === activeLessonId()) ?? null,
  );

  return (
    <Show
      when={!activeLessonId()}
      fallback={
        <div class="p-4 md:p-6">
          <Show when={activeLesson()}>
            {(l) => (
              <LessonPlayerLoader
                courseId={props.courseId}
                lesson={l()}
                progressId={progressByLesson().get(l().id)?.id}
                resumeBlockId={progressByLesson().get(l().id)?.lastBlockId ?? undefined}
                courseCompleted={completedCount() + 1 >= totalLessons()}
                onExit={() => setActiveLessonId(null)}
              />
            )}
          </Show>
        </div>
      }
    >
      <div class="mx-auto max-w-2xl space-y-6 p-4 md:p-6">
        <Link
          to="/learn"
          class="inline-flex items-center gap-1 text-sm font-medium text-muted-foreground transition-colors hover:text-accent"
        >
          ← {t(msg`My courses`)}
        </Link>

        {/* Cabecera del curso con progreso global */}
        <Show when={!course.isLoading} fallback={<Skeleton class="h-28 w-full rounded-3xl" />}>
          <div class="space-mesh rounded-3xl border border-border p-5 md:p-6">
            <h1
              class="font-display text-2xl font-extrabold tracking-tight"
              data-testid="learn-course-title"
            >
              {course.data?.title ?? t(msg`Course`)}
            </h1>
            <div class="mt-4 flex flex-wrap items-center gap-3">
              <HeaderStat
                icon={<Star class="h-4 w-4" />}
                value={stats.data?.total_xp ?? 0}
                label="XP"
                testid="learn-xp"
                cls="bg-joy-sun/20 text-foreground"
              />
              <HeaderStat
                icon={<Flame class="h-4 w-4" />}
                value={stats.data?.current_streak ?? 0}
                label={t(msg`streak`)}
                testid="learn-streak"
                cls="bg-joy-flame/15 text-joy-flame"
              />
              <HeaderStat
                icon={<Check class="h-4 w-4" />}
                value={`${completedCount()}/${totalLessons()}`}
                label={t(msg`lessons`)}
                testid="learn-completed"
                cls="bg-accent/12 text-accent"
              />
            </div>
            {/* Barra de progreso del curso */}
            <div class="mt-4 h-2.5 overflow-hidden rounded-full bg-muted/60">
              <div
                class="h-full rounded-full bg-gradient-to-r from-accent to-joy-berry transition-all duration-500"
                style={{
                  width: `${totalLessons() ? (completedCount() / totalLessons()) * 100 : 0}%`,
                }}
              />
            </div>
          </div>
        </Show>

        {/* Certificado: el curso está completo → enlaza al certificado emitido
            por el servidor (llega por el stream my_certificates). Un certificado
            SINCRONIZADO también manda por sí solo: el cierre de cohorte (F3-B)
            emite server-side con su propio criterio (gradebook), no con el
            criterio local de lecciones. */}
        <Show when={courseDone() || certificate.data}>
          <div
            class="flex flex-wrap items-center justify-between gap-3 rounded-3xl border-2 border-joy-sun/50 bg-joy-sun/10 p-5"
            data-testid="certificate-section"
          >
            <div class="flex items-center gap-3">
              <span class="flex h-11 w-11 items-center justify-center rounded-full bg-joy-sun/25 text-foreground">
                <Award class="h-6 w-6" />
              </span>
              <div>
                <p class="font-display font-bold">{t(msg`Course completed!`)}</p>
                <p class="text-xs text-muted-foreground">
                  <Show
                    when={certificate.data}
                    fallback={t(msg`Your certificate is being issued…`)}
                  >
                    {t(msg`Your certificate is ready.`)}
                  </Show>
                </p>
              </div>
            </div>
            <Show when={certificate.data}>
              {(cert) => (
                <Link
                  to={`/certificates/${cert().id}`}
                  data-testid="certificate-link"
                  class="rounded-2xl bg-accent px-5 py-2.5 text-sm font-bold text-accent-foreground shadow-md shadow-accent/25 transition-transform hover:scale-105 active:scale-95"
                >
                  {t(msg`View certificate`)}
                </Link>
              )}
            </Show>
          </div>
        </Show>

        {/* La senda: unidades con nodos-lección serpenteantes */}
        <div class="space-y-8" data-testid="learn-syllabus">
          <For each={units.data ?? []}>
            {(u) => (
              <section>
                <div class="mb-3 flex items-center gap-2">
                  <span class="h-6 w-1.5 rounded-full bg-accent" />
                  <h3 class="font-display text-lg font-bold">{u.title}</h3>
                </div>
                <ol class="relative flex flex-col items-center gap-1">
                  <For each={spineByUnit().get(u.id) ?? []}>
                    {(entry, i) => {
                      // Serpenteo: alterna izquierda/centro/derecha.
                      const shift = () =>
                        ["translate-x-0", "translate-x-14", "-translate-x-14"][i() % 3];
                      // FLAGS: kind deshabilitado por la academia/curso → el
                      // contenido pre-existente se muestra como parada
                      // "deshabilitado" (datos preservados), no desaparece.
                      const kindState = () => kindFeature()(entry.kind);
                      if (entry.kind !== "lesson" && !kindState().enabled) {
                        return (
                          <li
                            class={`flex w-full max-w-sm flex-col items-center ${shift()}`}
                            data-testid="learn-disabled-module"
                            data-kind={entry.kind}
                          >
                            <span class="flex h-12 w-12 items-center justify-center rounded-2xl border-2 border-dashed border-border bg-muted/40 text-muted-foreground">
                              <ToggleLeft class="h-5 w-5" />
                            </span>
                            <span class="mt-1.5 max-w-[11rem] text-center text-xs font-semibold text-muted-foreground">
                              {t(msg`Disabled by your academy`)}
                            </span>
                          </li>
                        );
                      }
                      if (entry.kind === "lesson" && entry.lesson) {
                        const l = entry.lesson;
                        const p = () => progressByLesson().get(l.id);
                        const done = () => p()?.status === "completed";
                        const current = () => currentLessonId() === l.id;
                        return (
                          <li class={`flex flex-col items-center ${shift()}`}>
                            <button
                              type="button"
                              data-testid="learn-lesson"
                              data-lesson-done={done() ? "1" : "0"}
                              onClick={() => setActiveLessonId(l.id)}
                              class="group flex flex-col items-center gap-1.5"
                              aria-label={l.title ?? t(msg`Lesson`)}
                            >
                              <span
                                class="relative flex h-16 w-16 items-center justify-center rounded-full border-4 shadow-md transition-transform group-hover:scale-105 group-active:scale-95"
                                classList={{
                                  "border-success bg-success text-success-foreground": done(),
                                  "border-accent bg-accent text-accent-foreground shadow-accent/30":
                                    !done() && current(),
                                  "border-border bg-surface text-muted-foreground":
                                    !done() && !current(),
                                }}
                              >
                                <Show
                                  when={done()}
                                  fallback={<Play class="h-6 w-6 translate-x-0.5" />}
                                >
                                  <Check class="h-7 w-7" stroke-width={3} />
                                </Show>
                                <Show when={current()}>
                                  <span class="absolute -inset-1.5 -z-10 animate-ping rounded-full bg-accent/40" />
                                </Show>
                              </span>
                              <span
                                class="max-w-[9rem] text-center text-xs font-semibold leading-tight"
                                classList={{ "text-muted-foreground": !done() && !current() }}
                              >
                                {l.title}
                              </span>
                              <span class="inline-flex items-center gap-1 text-[11px] font-bold text-foreground">
                                <Star class="h-3 w-3 fill-current text-joy-sun" /> {l.xp_reward} XP
                              </span>
                            </button>
                          </li>
                        );
                      }
                      // MEDIA-F3F: módulo `video` — parada de vídeo con
                      // reanudación ("continuar en 12:34") si hay progreso.
                      if (entry.kind === "video" && entry.module) {
                        const m = entry.module;
                        const open = () => openResourceId() === m.id;
                        const watch = () => watchByVideo().get(videoAssetIdOf(m));
                        const resumeAt = () => {
                          const w = watch();
                          return w && !w.completed_at && (w.position_seconds ?? 0) > 3
                            ? formatTime(w.position_seconds ?? 0)
                            : null;
                        };
                        const watched = () => Boolean(watch()?.completed_at);
                        return (
                          <li class={`flex w-full max-w-sm flex-col items-center ${shift()}`}>
                            <button
                              type="button"
                              data-testid="learn-video"
                              data-video-done={watched() ? "1" : "0"}
                              onClick={() => setOpenResourceId(open() ? null : m.id)}
                              class="group flex flex-col items-center gap-1.5"
                            >
                              <span
                                class="flex h-12 w-12 items-center justify-center rounded-2xl border-2 transition-transform group-hover:scale-105"
                                classList={{
                                  "border-success/50 bg-success/10 text-success": watched(),
                                  "border-joy-sky/40 bg-joy-sky/10 text-joy-sky": !watched(),
                                }}
                              >
                                <Film class="h-5 w-5" />
                              </span>
                              <span class="text-xs font-semibold text-muted-foreground">
                                {t(msg`Video`)}
                              </span>
                              <Show when={resumeAt()}>
                                {(at) => (
                                  <span
                                    class="rounded-full bg-accent/12 px-2 py-0.5 text-[11px] font-bold text-accent"
                                    data-testid="learn-video-resume"
                                  >
                                    {t(msg`resume at {at}`, { at: at() })}
                                  </span>
                                )}
                              </Show>
                            </button>
                            <Show when={open()}>
                              <div class="mt-2 w-full">
                                <VideoModuleViewer
                                  courseId={props.courseId}
                                  moduleId={m.id}
                                  refId={m.ref_id}
                                />
                              </div>
                            </Show>
                          </li>
                        );
                      }
                      // F3-B: módulo `assignment` — parada de tarea evaluable
                      // (entrega texto/archivo + nota devuelta).
                      if (entry.kind === "assignment" && entry.module) {
                        const m = entry.module;
                        const open = () => openResourceId() === m.id;
                        return (
                          <li class={`flex w-full max-w-sm flex-col items-center ${shift()}`}>
                            <button
                              type="button"
                              data-testid="learn-assignment"
                              onClick={() => setOpenResourceId(open() ? null : m.id)}
                              class="group flex flex-col items-center gap-1.5"
                            >
                              <span class="flex h-12 w-12 items-center justify-center rounded-2xl border-2 border-joy-sun/40 bg-joy-sun/10 text-foreground transition-transform group-hover:scale-105">
                                <ClipboardCheck class="h-5 w-5" />
                              </span>
                              <span class="text-xs font-semibold text-muted-foreground">
                                {t(msg`Assignment`)}
                              </span>
                            </button>
                            <Show when={open()}>
                              <div class="mt-2 w-full">
                                <AssignmentModulePlayer
                                  courseId={props.courseId}
                                  moduleId={m.id}
                                  refId={m.ref_id}
                                />
                              </div>
                            </Show>
                          </li>
                        );
                      }
                      // F3-C: módulo `forum` — parada de discusión de la cohorte.
                      if (entry.kind === "forum" && entry.module) {
                        const m = entry.module;
                        const open = () => openResourceId() === m.id;
                        return (
                          <li class={`flex w-full max-w-sm flex-col items-center ${shift()}`}>
                            <button
                              type="button"
                              data-testid="learn-forum"
                              onClick={() => setOpenResourceId(open() ? null : m.id)}
                              class="group flex flex-col items-center gap-1.5"
                            >
                              <span class="flex h-12 w-12 items-center justify-center rounded-2xl border-2 border-accent/40 bg-accent/10 text-accent transition-transform group-hover:scale-105">
                                <MessageSquare class="h-5 w-5" />
                              </span>
                              <span class="text-xs font-semibold text-muted-foreground">
                                {t(msg`Forum`)}
                              </span>
                            </button>
                            <Show when={open()}>
                              <div class="mt-2 w-full">
                                <ForumModuleViewer
                                  courseId={props.courseId}
                                  moduleId={m.id}
                                  refId={m.ref_id}
                                />
                              </div>
                            </Show>
                          </li>
                        );
                      }
                      // F3-G: módulo `live_session` — parada de clase en vivo
                      // en la senda: info de la sesión enlazada, "Entrar"
                      // cuando está EN VIVO y "Ver grabación" cuando existe.
                      if (entry.kind === "live_session" && entry.module) {
                        const m = entry.module;
                        const open = () => openResourceId() === m.id;
                        return (
                          <li class={`flex w-full max-w-sm flex-col items-center ${shift()}`}>
                            <button
                              type="button"
                              data-testid="learn-live-session"
                              onClick={() => setOpenResourceId(open() ? null : m.id)}
                              class="group flex flex-col items-center gap-1.5"
                            >
                              <span class="flex h-12 w-12 items-center justify-center rounded-2xl border-2 border-primary/40 bg-primary/10 text-primary transition-transform group-hover:scale-105">
                                <Radio class="h-5 w-5" />
                              </span>
                              <span class="text-xs font-semibold text-muted-foreground">
                                {t(msg`Live class`)}
                              </span>
                            </button>
                            <Show when={open()}>
                              <div class="mt-2 w-full">
                                <LiveSessionModuleViewer
                                  courseId={props.courseId}
                                  moduleId={m.id}
                                  refId={m.ref_id}
                                />
                              </div>
                            </Show>
                          </li>
                        );
                      }
                      // Módulo NO-lección (resource y futuros kinds F3): parada
                      // de material en la senda, en su posición.
                      const m = entry.module!;
                      const open = () => openResourceId() === m.id;
                      return (
                        <li class={`flex w-full max-w-sm flex-col items-center ${shift()}`}>
                          <button
                            type="button"
                            data-testid="learn-resource"
                            onClick={() => setOpenResourceId(open() ? null : m.id)}
                            class="group flex flex-col items-center gap-1.5"
                          >
                            <span class="flex h-12 w-12 items-center justify-center rounded-2xl border-2 border-joy-berry/40 bg-joy-berry/10 text-joy-berry transition-transform group-hover:scale-105">
                              <BookOpen class="h-5 w-5" />
                            </span>
                            <span class="text-xs font-semibold text-muted-foreground">
                              {t(msg`Material`)}
                            </span>
                          </button>
                          <Show when={open()}>
                            <div class="mt-2 w-full">
                              <ResourceModuleViewer
                                courseId={props.courseId}
                                moduleId={m.id}
                                refId={m.ref_id}
                              />
                            </div>
                          </Show>
                        </li>
                      );
                    }}
                  </For>
                </ol>
              </section>
            )}
          </For>
          <Show when={!course.isLoading && totalLessons() === 0}>
            <div class="flex flex-col items-center gap-3 rounded-3xl border-2 border-dashed border-border p-12 text-center text-muted-foreground">
              <Mascot mood="sleep" class="h-20 w-20" />
              <p>{t(msg`This course has no synced lessons yet.`)}</p>
            </div>
          </Show>
        </div>
      </div>
    </Show>
  );
};

/** Carga los bloques de la lección activa y monta el player. */
const LessonPlayerLoader: Component<{
  courseId: string;
  lesson: Lesson;
  progressId?: string;
  resumeBlockId?: string;
  courseCompleted: boolean;
  onExit: () => void;
}> = (props) => {
  const { t } = useI18n();
  const blocks = useLessonBlocks(() => props.lesson.id);
  return (
    <Show
      when={!blocks.isLoading}
      fallback={<Skeleton class="mx-auto h-[60vh] max-w-2xl rounded-3xl" />}
    >
      <LessonPlayer
        courseId={props.courseId}
        lessonId={props.lesson.id}
        lessonTitle={props.lesson.title ?? t(msg`Lesson`)}
        xpReward={props.lesson.xp_reward ?? 10}
        blocks={blocks.data ?? []}
        progressId={props.progressId}
        resumeBlockId={props.resumeBlockId}
        courseCompleted={props.courseCompleted}
        onExit={props.onExit}
      />
    </Show>
  );
};

const HeaderStat: Component<{
  icon: JSX.Element;
  label: string;
  value: JSX.Element;
  testid: string;
  cls: string;
}> = (props) => (
  <div
    class={`flex items-center gap-2 rounded-2xl px-3.5 py-1.5 ${props.cls}`}
    data-testid={props.testid}
  >
    <span class="opacity-90">{props.icon}</span>
    <span class="font-display text-lg font-extrabold leading-none">{props.value}</span>
    <span class="text-xs font-medium opacity-80">{props.label}</span>
  </div>
);
